// Api endpoint url builders

import * as QueryString from 'query-string';
import * as _ from 'lodash';

import * as AppUtilities from '../common/apputilities';
import AppConfiguration from './configuration';
import { LookupKeyParameterName } from './apilookupkeys';

export default abstract class ApiEndpoints {
    public static getApiBaseUrl() {
        // Returns the base API url to use for requests based on environment (production|any)
        const apiBaseUrl = AppConfiguration.getConfigurationValue(AppConfiguration.appConfiguration.apiBaseUrl);

        // Set redirectUri
        let re = /{BASE_URI_NO_PORT}/gi;
        let result = apiBaseUrl.replace(re, `${window.location.protocol}//${window.location.hostname}`);

        // Hack for surge.sh deploy
        const surgeConfig = AppConfiguration.getConfigurationValue(AppConfiguration.appConfiguration.surge);
        if (window.location.hostname.toLowerCase() === surgeConfig.projectDomain
            || window.location.hostname.toLowerCase() === 'app.appliedreserveanalysis.com'
            || window.location.hostname.toLowerCase() === 'portal.complexsolutionsltd.com'
            || window.location.hostname.toLowerCase() === 'portal.appliedreserveanalysis.com') {
            result = apiBaseUrl.replace(re, `${window.location.protocol}//${surgeConfig.projectApiHostname}`);
        }

        return result;
    }

    public static composeApiUrl(resourcePath: string) {
        // Return the API request url for a given resourcePath (ex: 'association/12345')
        if (!resourcePath) { return ''; }

        let baseUrl = ApiEndpoints.getApiBaseUrl(); // .getConfigurationValue(AppConfiguration.appConfiguration.apiBaseUrl);

        const result = `${baseUrl}${resourcePath}`;

        return result;
    }

    // Endpoint urls
    public static downloadKMLUrl() {
        // Download XML file for active jobs dashboard formatted as KML
        return ApiEndpoints.composeApiUrl(`export/kml/convert`);
    }

    public static getNewClientInviteUrl() {
        // New client invite
        return ApiEndpoints.composeApiUrl(`clientinvite/new`);
    }

    public static getUpdateReportScenarioUrl() {
        // Update existing portal user report scenario
        return ApiEndpoints.composeApiUrl(`report/scenarios/update`);
    }

    public static getCopyReportScenarioUrl() {
        // Update/insert portal user report scenario
        return ApiEndpoints.composeApiUrl(`report/scenarios/copy`);
    }

    public static getDeleteReportScenarioUrl() {
        // Update/insert portal user report scenario
        return ApiEndpoints.composeApiUrl(`report/scenarios/delete`);
    }

    public static getToggleClientInviteUrl() {
        // New client invite
        return ApiEndpoints.composeApiUrl(`clientinvite/toggle`);
    }

    public static getResendClientInviteUrl() {
        // Resend client invite
        return ApiEndpoints.composeApiUrl(`clientinvite/resend`);
    }

    public static getUserDetailUrl(userObjId: string) {
        // Load user details
        return ApiEndpoints.composeApiUrl(`user/${userObjId}`);
    }

    public static getTextContentUrl(textContentObjId: string) {
        // Load text content details
        return ApiEndpoints.composeApiUrl(`lookup/textContents/getObj/${textContentObjId}`);
    }

    public static getUserActivationUrl(userObjId: string) {
        // Load user details for account activation
        return ApiEndpoints.composeApiUrl(`user/activate/${userObjId}`);
    }

    public static getMyAccountUserDetailUrl() {
        // Load current user's account profile
        return ApiEndpoints.composeApiUrl(`me/profile`);
    }

    public static getUpdateUserLastLoginUrl() {
        // Update current user's last login date
        return ApiEndpoints.composeApiUrl(`me/new-login`);
    }

    public static getAssociationDetailUrl(associationId: number) {
        // Load association detail
        return ApiEndpoints.composeApiUrl(`association/${associationId}`);
    }

    public static getUpdateAssociationUrl(associationId: number) {
        // Update existing association
        return ApiEndpoints.composeApiUrl(`association/${associationId}/update`);
    }

    public static getUpdateReserveStudyChartUrl(orderId: number) {
        // Update existing chart data for an order
        return ApiEndpoints.composeApiUrl(`reservestudychart/${orderId}/update`);
    }

    public static getUpdateComponentReplacementCostsUrl(componentId: number) {
        // Update component replacement costs
        return ApiEndpoints.composeApiUrl(`replacementcost/component/${componentId}/update`);
    }

    public static getAssociationInventoryUrl(associationId: number) {
        // Load association inventory
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory`);
    }

    public static getAssociationInventoryDetailUrl(associationId: number, inventoryId: number) {
        // Load association inventory detail
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory/${inventoryId}`);
    }

    public static getUpdateAssociationInventoryUrl(associationId: number, inventoryId: number) {
        // Update existing association
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory/${inventoryId}`);
    }

    public static getUpdateAssociationInventoryUploadSaveUrl(associationId: number, inventoryId: number) {
        // Upload new images to existing association inventory item
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory/${inventoryId}/upload/save`);
    }

    public static getUpdateAssociationInventoryUploadSaveUrlNew(associationId: number, inventoryId: number) {
        // Upload new images to existing association inventory item
        return ApiEndpoints.composeApiUrl(`FileUpload/association/${associationId}/inventory/${inventoryId}/save`);
    }

    public static getUpdateAssociationInventoryUploadRemoveUrl(associationId: number, inventoryId: number) {
        // Remove new images to existing association inventory item
        return ApiEndpoints.composeApiUrl(`FileUpload/association/${associationId}/inventory/${inventoryId}/remove`);
    }

    public static getNewAssociationInventoryUrl(associationId: number, inventoryId: number) {
        // Create a new existing association inventory item
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory/${inventoryId}/new`);
    }

    public static getDeleteAssociationInventoryUrl(associationId: number, inventoryId: number) {
        // Delete existing association inventory item
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory/${inventoryId}/delete`);
    }

    public static getNewAssociationInventoryResearchUrl(associationId: number, inventoryId: number) {
        // Create a new association inventory research record
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory/${inventoryId}/research/new`);
    }

    public static getDeleteAssociationInventoryResearchUrl(associationId: number, inventoryId: number, researchId: number) {
        // Delete association inventory research record
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory/${inventoryId}/research/${researchId}/delete`);
    }

    public static getNewAssociationInventorySubgroupUrl(associationId: number) {
        // Create a new association inventory subgroup
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory-subgroup/new`);
    }

    public static getUserInventoryTemplateUrl(userInventoryTemplateObjId: string) {
        // Load user inventory template details
        return ApiEndpoints.composeApiUrl(`userinventorytemplate/template/${userInventoryTemplateObjId}`);
    }

    public static getNewUserInventoryTemplateUrl() {
        // Create a new user inventory template
        return ApiEndpoints.composeApiUrl(`userinventorytemplate/create`);
    }

    public static getApplyUserInventoryTemplateUrl() {
        // Apply any existing user inventory template
        return ApiEndpoints.composeApiUrl(`userinventorytemplate/apply`);
    }

    public static getApplyRegionalComponentListTemplateUrl() {
        // Apply any existing regional component list template
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/apply`);
    }

    public static getUpdateUserInventoryTemplateUrl(userInventoryTemplateObjId: string) {
        // Update existing user inventory template document
        return ApiEndpoints.composeApiUrl(`userinventorytemplate/template/${userInventoryTemplateObjId}/update`);
    }

    public static getRenameUserInventoryTemplateUrl(userInventoryTemplateObjId: string) {
        // Update existing user inventory template document
        return ApiEndpoints.composeApiUrl(`userinventorytemplate/template/${userInventoryTemplateObjId}/rename`);
    }

    public static getDeleteUserInventoryTemplateUrl(userInventoryTemplateObjId: string) {
        // Update existing user inventory template document
        return ApiEndpoints.composeApiUrl(`userinventorytemplate/template/${userInventoryTemplateObjId}/delete`);
    }

    public static getDeleteUserInventoryTemplateItemUrl(userInventoryTemplateObjId: string, userInventoryTemplateInventoryObjId: string) {
        // Delete inventory item from user inventory template document
        return ApiEndpoints.composeApiUrl(`userinventorytemplate/template/${userInventoryTemplateObjId}/inventory/${userInventoryTemplateInventoryObjId}/delete`);
    }

    // Regional Component List endpoints
    public static getRegionalComponentListsUrl() {
        // Get all regional component lists
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/lists`);
    }

    public static getRegionalComponentListUrl(regionalComponentListId: string) {
        // Load regional component list details
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}`);
    }

    public static getNewRegionalComponentListUrl() {
        // Create a new regional component list
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/create`);
    }

    public static getCopyAssociationInventoryToRegionalComponentListUrl() {
        // Copy association inventory to regional component list
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/copy`);
    }

    public static getUpdateRegionalComponentListUrl(regionalComponentListId: string) {
        // Update existing regional component list document
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/update`);
    }

    public static getRenameRegionalComponentListUrl(regionalComponentListId: string) {
        // Rename existing regional component list document
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/rename`);
    }

    public static getDuplicateRegionalComponentListUrl(regionalComponentListId: string) {
        // Duplicate existing regional component list document
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/duplicate`);
    }

    public static getDeleteRegionalComponentListUrl(regionalComponentListId: string) {
        // Delete regional component list document
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/delete`);
    }

    public static getDeleteRegionalComponentListItemUrl(regionalComponentListId: string, regionalComponentListInventoryObjId: string) {
        // Delete inventory item from regional component list document
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/inventory/${regionalComponentListInventoryObjId}/delete`);
    }

    public static getNewRegionalComponentListItemUrl(regionalComponentListId: string) {
        // Create a new inventory item in the regional component list; returns new item id (and optionally updated list)
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/inventory/create`);
    }

    public static getRegionalComponentListInventoryDetailUrl(regionalComponentListId: string, regionalComponentListInventoryObjId: string) {
        // Load regional component list (with all items) for detail view; current item resolved by inventoryId in mapStateToProps
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/inventory/${regionalComponentListInventoryObjId}`);
    }

    public static getUpdateRegionalComponentListItemUrl(regionalComponentListId: string, regionalComponentListInventoryObjId: string) {
        // Update a single inventory item in the regional component list
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/inventory/${regionalComponentListInventoryObjId}/update`);
    }

    public static getDuplicateRegionalComponentListItemUrl(regionalComponentListId: string, regionalComponentListInventoryObjId: string) {
        // Duplicate an inventory item in the regional component list
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/inventory/${regionalComponentListInventoryObjId}/duplicate`);
    }

    public static getNewRegionalComponentListResearchUrl(regionalComponentListId: string, regionalComponentListInventoryObjId: string) {
        // Create a new research record for a regional list inventory item
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/inventory/${regionalComponentListInventoryObjId}/research/new`);
    }

    public static getDeleteRegionalComponentListResearchUrl(regionalComponentListId: string, regionalComponentListInventoryObjId: string, researchId: number) {
        // Delete a research record from a regional list inventory item
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/inventory/${regionalComponentListInventoryObjId}/research/${researchId}/delete`);
    }

    public static getNewRegionalComponentListSubgroupUrl(regionalComponentListId: string) {
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/inventory-subgroup/new`);
    }

    public static getUpdateRegionalComponentListSubgroupUrl(regionalComponentListId: string) {
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/inventory-subgroup/update`);
    }

    public static getDeleteRegionalComponentListSubgroupUrl(regionalComponentListId: string) {
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/inventory-subgroup/delete`);
    }

    public static getDuplicateRegionalComponentListSubgroupUrl(regionalComponentListId: string) {
        return ApiEndpoints.composeApiUrl(`regionalcomponentlist/list/${regionalComponentListId}/inventory-subgroup/duplicate`);
    }

    public static getUpdateAssociationInventorySubgroupUrl(associationId: number) {
        // Create a new association inventory subgroup
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory-subgroup/update`);
    }

    public static getDeleteAssociationInventorySubgroupUrl(associationId: number) {
        // Create a new association inventory subgroup
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory-subgroup/delete`);
    }

    public static getDuplicateAssociationInventoryUrl(associationId: number, inventoryId: number) {
        // Create a new existing association inventory item
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory/${inventoryId}/duplicate`);
    }

    public static getDuplicateAssociationInventorySubgroupUrl(associationId: number) {
        // Duplicates association inventory subgroup to target
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory-subgroup/duplicate`);
    }

    public static getUpdateAssociationInventoryItemsUrl(associationId: number) {
        // Update association inventory items
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory-items/update`);
    }

    public static getDeleteAssociationInventoryItemsUrl(associationId: number) {
        // Delete association inventory items
        return ApiEndpoints.composeApiUrl(`association/${associationId}/inventory-items/delete`);
    }

    public static getUpdateOrderUrl(orderId: number) {
        // Update existing association order
        return ApiEndpoints.composeApiUrl(`order/${orderId}`);
    }

    public static getUpdateOrdersSetAsPaidUrl() {
        // Update existing order to set coordinator paid status
        return ApiEndpoints.composeApiUrl(`order/coordinator-jobs/inspector-paid`);
    }

    public static getDeleteOrderUrl(orderId: number) {
        // Delete existing order
        // return ApiEndpoints.composeApiUrl(`order/setaspaid/${orderId}/delete`); // Removed 20191217
        return ApiEndpoints.composeApiUrl(`order/${orderId}/delete`);
    }

    public static getCreateNext3YearPlanSeqOrderUrl(orderId: number) {
        // Create the next sequenced order for current 3 year plan order
        return ApiEndpoints.composeApiUrl(`order/${orderId}/order-services/3-year-plan/create-next-order`);
    }

    public static getNewOrderUrl(associationId: number, clientId: number = -1) {
        // Create new association order
        return ApiEndpoints.composeApiUrl(`order/new/${associationId}/${clientId}`);
    }

    public static getUpdateWonJobsUrl() {
        // Update won jobs (sent invoice/entered into quickbooks)
        return ApiEndpoints.composeApiUrl(`order/won-jobs/update`);
    }

    public static getNewInventoryComponentUrl(componentObjId: string = 'new') {
        // Create new component inventory component
        return ApiEndpoints.composeApiUrl(`component/${componentObjId}/new`);
    }

    public static getUpdateLookupObjUrl(tableName: string, objId: string) {
        // Update existing lookup table document
        return ApiEndpoints.composeApiUrl(`lookup/${tableName}/${objId}/update`);
    }

    public static getNewLookupObjUrl(tableName: string) {
        // Create lookup table document
        return ApiEndpoints.composeApiUrl(`lookup/${tableName}/new`);
    }

    public static getDeleteLookupObjUrl(tableName: string, objId: string) {
        // Delete existing lookup table document
        return ApiEndpoints.composeApiUrl(`lookup/${tableName}/${objId}/delete`);
    }

    public static getUpdateOutstandingBidUrl(orderId: number) {
        // Update existing association order/bid
        return ApiEndpoints.composeApiUrl(`order/${orderId}/outstanding-bid`);
    }

    public static getUpdateReminderBidUrl(orderId: number) {
        // Update existing association order/bid
        return ApiEndpoints.composeApiUrl(`order/${orderId}/reminder-bid`);
    }

    public static getUpdateReminderBidsReminderDateUrl() {
        // Update existing reminder bids reminder dates
        return ApiEndpoints.composeApiUrl(`order/reminder-bids/reminder-date`);
    }

    public static getUpdateReminderBidsSendReminderEmailUrl() {
        // Update existing reminder bids reminder dates
        return ApiEndpoints.composeApiUrl(`order/reminder-bids/bulk-send`);
    }

    public static getUpdateReminderBids3YearPlanStatusUrl() {
        // Update existing reminder bids for 3 year plan bids status
        return ApiEndpoints.composeApiUrl(`order/reminder-bids/3-year-plan-status/update`);
    }

    public static getSetBidsSentUrl() {
        // Update existing bids to mark as sent
        return ApiEndpoints.composeApiUrl(`order/send-bids/update`);
    }

    public static getUpdateUserUrl(userObjId: string) {
        // Update existing user document
        return ApiEndpoints.composeApiUrl(`user/${userObjId}`);
    }

    public static getUpdateComponentUrl(componentObjId: string) {
        // Update existing component document
        return ApiEndpoints.composeApiUrl(`component/${componentObjId}/update`);
    }

    public static getUpdateReplacementCostUrl(replacementCostObjId: string) {
        // Update existing replacement cost document
        return ApiEndpoints.composeApiUrl(`replacementcost/${replacementCostObjId}/update`);
    }

    public static getNewReplacementCostUrl(componentObjId: string) {
        // Create new replacement cost document for component
        return ApiEndpoints.composeApiUrl(`replacementcost/new/component/${componentObjId}`);
    }

    public static getNewStandardEvaluationUrl(componentObjId: string) {
        // Create new standard evaluation child document for component
        return ApiEndpoints.composeApiUrl(`Component/${componentObjId}/standardevaluation/new/`);
    }

    public static getUpdateStandardEvaluationUrl(componentObjId: string) {
        // Updates standard evaluation child document for component
        return ApiEndpoints.composeApiUrl(`Component/${componentObjId}/standardevaluation/update/`);
    }

    public static getDeleteStandardEvaluationUrl(componentObjId: string) {
        // Deletes standard evaluation child document for component
        return ApiEndpoints.composeApiUrl(`Component/${componentObjId}/standardevaluation/delete/`);
    }

    public static getDeleteReplacementCostUrl(replacementCostObjId: string) {
        // Delete existing replacement cost document
        return ApiEndpoints.composeApiUrl(`replacementcost/${replacementCostObjId}/delete`);
    }

    public static getNewClientUrl() {
        return ApiEndpoints.composeApiUrl(`client/new/new-client`);
    }

    public static getUpdateClientUrl(clientId: number) {
        // Update existing client
        return ApiEndpoints.composeApiUrl(`client/${clientId}/update`);
    }

    public static getNewAssociationUrl() {
        return ApiEndpoints.composeApiUrl(`association/new/new-association`);
    }

    public static getDeleteAssociationUrl(assocId: number) {
        return ApiEndpoints.composeApiUrl(`association/delete/${assocId}`);
    }

    public static getNewUserUrl() {
        // Create a new user
        return ApiEndpoints.composeApiUrl(`user/new/new-user`);
    }

    public static getNewUserResumeUrl() {
        // Create a new user resume
        return ApiEndpoints.composeApiUrl(`user/new/new-user-resume`);
    }

    public static getUpdateUserAdminResetPasswordUrl(username: string) {
        // Update existing user document
        return ApiEndpoints.composeApiUrl(`user/${username}/update/admin-reset-password`);
    }

    public static getUpdateRegionUrl(regionId: number) {
        // Update existing user document
        return ApiEndpoints.composeApiUrl(`region/${regionId}`);
    }

    public static getReserveStudyUrl(associationId: number, archivedOrderId?: number) {
        // Load association reserve study charge
        return (archivedOrderId) ? ApiEndpoints.composeApiUrl(`association/${associationId}/reserve-study/${archivedOrderId}`) : ApiEndpoints.composeApiUrl(`association/${associationId}/reserve-study`);
    }

    public static getSearchAssociationsUrl() {
        // Load search association
        return ApiEndpoints.composeApiUrl(`search/association`);
    }

    public static getSearchClientDashboardUrl() {
        // Load search association
        return ApiEndpoints.composeApiUrl(`search/client-dashboard`);
    }

    public static getSearchClientsUrl() {
        // Load search client
        return ApiEndpoints.composeApiUrl(`search/client`);
    }

    public static getSearchClientContactsUrl() {
        // Load search client contacts
        return ApiEndpoints.composeApiUrl(`search/client-contacts`);
    }

    public static getSearchUsersUrl() {
        // Load search users
        return ApiEndpoints.composeApiUrl(`search/user`);
    }

    public static getClientDetailUrl(clientId: number, includeAssociations: boolean = false) {
        // Load client detail
        let result = ApiEndpoints.composeApiUrl(`client/${clientId}`);

        // result += `?${QueryString.stringify({ includeAssociations: includeAssociations })}`;

        return result;
    }

    public static getCreateClientUrl() {
        // Insert/create new client
        return ApiEndpoints.composeApiUrl(`client/create`);
    }

    public static getCreateClientContactUrl(id: number) {
        // Insert/create new client contact
        return ApiEndpoints.composeApiUrl(`client/${id}/contacts/create`);
    }

    public static getUpdateClientContactUrl(id: number) {
        // Update/edit new client contact
        return ApiEndpoints.composeApiUrl(`client/${id}/contacts/update`);
    }

    public static getOrderDetailUrl(id: number) {
        // Load order detail
        return ApiEndpoints.composeApiUrl(`order/${id}`);
    }

    public static getEditOrderDetailUrl(id: number) {
        // Load order edit detail
        return ApiEndpoints.composeApiUrl(`order/${id}/edit`);
    }

    public static getEditComponentDetailUrl(componentObjId: string) {
        // Load component edit detail
        return ApiEndpoints.composeApiUrl(`component/${componentObjId}/edit`);
    }

    public static getOrderBidLetterDataUrl(id: number) {
        // Load order bid letter detail for single order by orderid
        return ApiEndpoints.composeApiUrl(`order/${id}/prepare-bid-letter`);
    }

    public static getOrdersBidLettersDataUrl(orderObjIdsCSV: string) {
        // Load bid letter detail for multiple orders by order document obj ids (csv)
        return ApiEndpoints.composeApiUrl(`order/multiple/${orderObjIdsCSV}/prepare-bid-letters`);
    }

    public static getActiveOrdersSearchUrl() {
        // Load active orders
        return ApiEndpoints.composeApiUrl(`order/active`);
    }

    public static getOrdersSearchFiltersUrl() {
        // Load search orders
        return ApiEndpoints.composeApiUrl(`search/order/filters`);
    }

    public static getOrdersSearchUrl() {
        // Load search orders
        return ApiEndpoints.composeApiUrl(`search/order`);
    }

    public static getCoordinatorJobsSearchUrl() {
        // Load search coordinator jobs
        return ApiEndpoints.composeApiUrl(`search/coordinator-jobs`);
    }

    public static getActiveJobsSearchUrl() {
        // Load search active jobs
        return ApiEndpoints.composeApiUrl(`search/active-jobs`);
    }

    public static getPreparedBidsSearchUrl() {
        // Load search prepared bids
        return ApiEndpoints.composeApiUrl(`search/prepared-bids`);
    }

    public static getSendBidsSearchUrl() {
        // Load search send bids
        return ApiEndpoints.composeApiUrl(`search/prepared-bids`);
    }

    public static getAssociationExportOrdersFiltersSearchUrl() {
        // Load search association orders filters for export grid
        return ApiEndpoints.composeApiUrl(`search/association-export-orders/filters`);
    }

    public static getAssociationExportOrdersSearchUrl() {
        // Load search association orders for export grid
        return ApiEndpoints.composeApiUrl(`search/association-export-orders`);
    }

    public static getResearchComponentsSearchUrl() {
        // Load search research components
        return ApiEndpoints.composeApiUrl(`search/research-components`);
    }

    public static getResearchBidsSearchUrl() {
        // Load search bids
        return ApiEndpoints.composeApiUrl(`search/research-bids`);
    }

    public static getResearchBidsFiltersUrl() {
        // Load filter options for research bids search
        return ApiEndpoints.composeApiUrl(`search/research-bids/filters`);
    }

    public static getResearchBidsExportAllUrl() {
        // Fetch ALL research bids (no pagination) for Google Earth / KML export
        return ApiEndpoints.composeApiUrl(`search/research-bids/export-all`);
    }

    public static getNewResearchBidsSearchUrl() {
        // Load search new research bids
        return ApiEndpoints.composeApiUrl(`search/new-research-bids`);
    }

    public static getOutstandingBidsSearchFiltersUrl() {
        // Load search filters bids
        return ApiEndpoints.composeApiUrl(`search/outstanding-bids/filters`);
    }

    public static getOutstandingBidsSearchUrl() {
        // Load search bids
        return ApiEndpoints.composeApiUrl(`search/outstanding-bids`);
    }

    public static getReminderBidsSearchUrl() {
        // Load reminder bids
        return ApiEndpoints.composeApiUrl(`search/reminder-bids`);
    }

    public static getWonJobsSearchUrl() {
        // Load search won jobs
        return ApiEndpoints.composeApiUrl(`search/won-jobs`);
    }

    public static getDefaultManagersSearchUrl() {
        // Load search region default managers
        return ApiEndpoints.composeApiUrl(`search/default-managers`);
    }

    public static getEditRegionUrl(id: number) {
        // Load region
        return ApiEndpoints.composeApiUrl(`region/${id}/edit`);
    }

    public static getReportForAssociationUrl(id: number) {
        // Load association analysis report data
        return ApiEndpoints.composeApiUrl(`report/association/${id}`);
    }

    public static getProxiedImageUrl(imageUrl: string) {
        // Image proxied through API
        return ApiEndpoints.composeApiUrl(`imageproxy?url=${imageUrl}`);
    }

    public static getInventoryPictureUrl(picturePath: string, thumb: boolean = false, thumbOptions: string = 'w_200,h_150,c_fill,f_auto/') {
        // picturePath: '/Photos/10072C - Mission Verde/DSC02710c.jpg'

        // Hack for surge.sh deploy
        const picturesConfig = AppConfiguration.getConfigurationValue(AppConfiguration.appConfiguration.inventoryPictures);

        const amazonBase = picturesConfig.amazonBucketBaseUrl;
        const cloudinaryBase = picturesConfig.cloudinaryFetchBaseUrl;

        const url = `${cloudinaryBase}${(thumb) ? thumbOptions : 'f_auto/'}${amazonBase}${picturePath}`;

        // console.log(`apiendpoints.tsx::getInventoryPictureUrl picturePath "${picturePath}" thumb ${thumb} returnUrl ${url}`);

        return url;
    }

    public static getBidLetterUserSignaturePictureUrl(picturePath: string, thumb: boolean = false, thumbOptions: string = 'w_150,h_55,f_auto/') {
        // Hack for surge.sh deploy
        const picturesConfig = AppConfiguration.getConfigurationValue(AppConfiguration.appConfiguration.inventoryPictures);

        const amazonBase = picturesConfig.amazonBucketBaseUrl;
        const cloudinaryBase = picturesConfig.cloudinaryFetchBaseUrl;

        const url = `${cloudinaryBase}${(thumb) ? thumbOptions : 'f_auto/'}${amazonBase}${picturePath}`;

        return url;
    }

    public static getAppOrderPictureUrl = (picturePath: string, thumb: boolean = false, proxy: boolean = false) => {
        /* fetch image from aws s3 bucket, apply transformations:
            OLD: https://res.cloudinary.com/complex-solutions/image/fetch/w_200/https://s3.us-east-2.amazonaws.com/ns8-complex-solutions/Photos/10072C+-+Mission+Verde/DSC02710c.jpg
            NEW: https://res.cloudinary.com/complex-app/image/fetch/w_200/https://s3.us-east-2.amazonaws.com/ns8-complex-solutions/Photos/10072C+-+Mission+Verde/DSC02710c.jpg	    
            */
        // let result = 'https://app.complexsolutionsltd.com';
        let result = 'http://app.complexsolutionsltd.com';
        if (thumb) { result += '/thumbs'; }

        result += picturePath;

        // if (proxy) {
        //     result = ApiEndpoints.getProxiedImageUrl(result);
        // }

        // console.log(`apiendpoints.tsx::getAppOrderPictureUrl picturePath "${picturePath}" thumb ${thumb} proxy ${proxy} returnUrl ${result}`);

        return result;
    }

    public static getLookupsByKeyUrl(lookupKeys: Array<string> = []) {
        // Compose lookups querystring, appending lookupKey values
        let result = ApiEndpoints.composeApiUrl('lookup/getbykey');

        // Compose querystring
        if (AppUtilities.isArrayWithLength(lookupKeys)) {
            const queryParams = _.set({}, LookupKeyParameterName, lookupKeys);
            const queryString = QueryString.stringify(queryParams);

            if (queryString) { result += `?${queryString}`; }
        }

        return result;
    }

    public static getLookupsByKeyWithChildObjIdUrl(lookupKey: string = '', childObjId: string = '') {
        // Compose lookups querystring, appending lookupKey values
        let result = ApiEndpoints.composeApiUrl(`lookup/getbykey/${lookupKey}/${childObjId}`);

        return result;
    }
}



// WEBPACK FOOTER //
// ./src/config/apiendpoints.tsx